/**
 * Active Notification View
 *
 * Shared contract for foreground notification suppression.
 *
 * Primary responsibilities:
 * - Validate active-view scopes and tab identifiers
 * - Read suppression metadata from notification payloads
 * - Expose heartbeat timing for focused browser tabs
 *
 * Dependencies:
 * - None
 */

export const ACTIVE_NOTIFICATION_VIEW_SCOPE_TYPES = [
  'chat',
  'space',
  'space_task',
  'space_conversation'
] as const

export type NotificationActiveViewScopeType =
  (typeof ACTIVE_NOTIFICATION_VIEW_SCOPE_TYPES)[number]

export type NotificationActiveViewScope = {
  scopeType: NotificationActiveViewScopeType
  scopeId: string
}

export const ACTIVE_NOTIFICATION_VIEW_HEARTBEAT_MS = 15_000
export const ACTIVE_NOTIFICATION_VIEW_TTL_MS = 45_000
export const ACTIVE_NOTIFICATION_VIEW_TAB_STORAGE_KEY =
  'losi:notification-active-view:tab-id'

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const TAB_ID_PATTERN = /^[A-Za-z0-9:_-]{1,128}$/

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function isNotificationActiveViewScopeType(
  value: unknown
): value is NotificationActiveViewScopeType {
  return (
    typeof value === 'string' &&
    ACTIVE_NOTIFICATION_VIEW_SCOPE_TYPES.includes(
      value as NotificationActiveViewScopeType
    )
  )
}

export function isNotificationActiveViewScopeId(
  value: unknown
): value is string {
  return typeof value === 'string' && UUID_PATTERN.test(value.trim())
}

export function normalizeNotificationActiveViewTabId(value: unknown) {
  if (typeof value !== 'string') {
    return null
  }

  const tabId = value.trim()
  return TAB_ID_PATTERN.test(tabId) ? tabId : null
}

export function readNotificationActiveViewScope(
  value: unknown
): NotificationActiveViewScope | null {
  if (!isRecord(value)) {
    return null
  }

  const scopeType = value.scopeType
  const scopeId = value.scopeId

  if (
    !isNotificationActiveViewScopeType(scopeType) ||
    !isNotificationActiveViewScopeId(scopeId)
  ) {
    return null
  }

  return {
    scopeType,
    scopeId: scopeId.trim()
  }
}

export function getNotificationActiveViewScope(
  data: Record<string, unknown>
): NotificationActiveViewScope | null {
  return readNotificationActiveViewScope(data.suppressIfActiveView)
}
